import {AnyAction, isAllOf} from '@reduxjs/toolkit';

import {TCurrency, buildQueryString, not, or, storage} from '@edna/utils';

import {EStorageKeys, TItemId} from 'src/constants';
import {ESpecialOffer} from 'src/containers/Notifications/SpecialOffers/definitions';
import {
  ECacheTag,
  isMatchQueryMeta,
  listenerMiddleware,
  rootApi,
  showErrorNotification,
} from 'src/models';

import {
  EFeatureFlag,
  EResellerIds,
  ERoles,
  EUserConfigCodes,
  TBankDetails,
  TPaymentType,
  TTenant,
  TTenantUser,
  TUser,
  TUserConfig,
} from './definitions';

const api = rootApi.injectEndpoints({
  overrideExisting: 'throw',
  endpoints: (builder) => ({
    sendUserFeedback: builder.mutation<
      void,
      {
        feedbackPage: string;
        feedback: string;
      }
    >({
      query: (data) => ({
        method: 'POST',
        url: '/user/feedback',
        data,
        meta: {
          isShowError: true,
        },
      }),
    }),
    getCurrentUser: builder.query<TUser, void>({
      query: () => ({
        method: 'GET',
        url: '/user/current',
      }),
      providesTags: [ECacheTag.USER],
    }),
    getAllTenantUsers: builder.query<TTenantUser[], void>({
      query: () => ({
        method: 'GET',
        url: '/user/list',
        meta: {isShowError: true, roles: not<ERoles>('ROLE_BASIC_USER')},
      }),
    }),
    getTenantUsers: builder.query<TTenantUser[], void>({
      query: () => ({
        method: 'GET',
        url: '/user/list-tenant-users-with-logins',
        meta: {isShowError: true, roles: not<ERoles>('ROLE_BASIC_USER')},
      }),
    }),
    getTenantData: builder.query<TTenant, void>({
      query: () => ({
        method: 'GET',
        url: '/tenantmanagement/tenant/data',
        meta: {isShowError: true, roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER')},
      }),
      providesTags: [ECacheTag.TENANT],
    }),
    getUserConfig: builder.query<TUserConfig, void>({
      query: () => ({
        method: 'GET',
        url: '/user/ui-settings',
      }),
    }),
    // принимает любой объект. Перетирает прежние значения
    setUserConfig: builder.mutation<void, TUserConfig>({
      query: (data) => ({
        method: 'POST',
        data,
        url: '/user/ui-settings',
        meta: {isShowError: true},
      }),
      async onQueryStarted(data, {dispatch, queryFulfilled}) {
        try {
          await queryFulfilled;

          dispatch(api.util.updateQueryData('getUserConfig', undefined, () => data));
        } catch {}
      },
    }),
    getWhatsAppTariffChangeBannerDates: builder.query<{startDate: string; endDate: string}, void>({
      query: () => ({
        method: 'GET',
        url: '/tenantmanagement/user/config/known',
        params: {
          code: EUserConfigCodes.TENANT_WHATSAPP_TARIFF_CHANGE_2025_BANNER_SHOW_DATES,
        },
      }),
    }),
    getBankDetails: builder.query<
      TBankDetails[],
      {currencyCode?: TCurrency; paymentType?: TPaymentType; resellerId: EResellerIds}
    >({
      query: ({currencyCode, resellerId, paymentType}) => ({
        method: 'GET',
        url: currencyCode
          ? `/tenantmanagement/bank-details/find${buildQueryString({
              currency: currencyCode,
              resellerId,
              paymentType,
            })}`
          : `/tenantmanagement/bank-details/by-reseller-id/${resellerId}`,
        meta: {isShowError: true},
      }),
    }),
    setUserTimeZone: builder.mutation<void, string>({
      query: (timeZone) => ({
        method: 'PUT',
        url: '/user/time-zone',
        data: {timeZone},
      }),
      invalidatesTags: [ECacheTag.USER],
    }),
    setSurveyConsent: builder.mutation<void, boolean>({
      query: (surveyConsentGranted) => ({
        method: 'PUT',
        url: '/user/survey-consent',
        data: {surveyConsentGranted},
      }),
      invalidatesTags: [ECacheTag.USER],
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    submitSurveyAnswers: builder.mutation<void, Record<number, any>>({
      query: (answers) => ({
        method: 'POST',
        url: '/user/survey',
        data: {
          version: 1,
          surveyType: 'login',
          answers,
        },
      }),
    }),
    getFeatureFlags: builder.query<Partial<Record<EFeatureFlag, boolean>>, void>({
      query: () => ({
        method: 'GET',
        url: `/tenantmanagement/unleash/feature-toggle-list/${Number(storage.get(EStorageKeys.TENANT_ID))}`,
        meta: {isShowError: true},
      }),
    }),
    getLowBalanceLimits: builder.query<Record<string, number>, void>({
      query: () => ({
        method: 'GET',
        url: '/tenantmanagement/user/config/known',
        params: {
          code: EUserConfigCodes.TENANT_NOTIFICATION_BALANCE_LOW_LIMITS,
        },
        meta: {isShowError: true},
      }),
    }),
    getSpecialOffers: builder.query<ESpecialOffer[], void>({
      query: () => ({
        method: 'GET',
        url: '/tenantmanagement/user/config/known',
        params: {
          code: EUserConfigCodes.TENANT_SPECIAL_OFFERS,
        },
        meta: {isShowError: true},
      }),
    }),
    acceptTariffs: builder.mutation<void, void>({
      query: () => ({
        method: 'POST',
        url: '/tenantmanagement/tenant/accept-tariffs',
        meta: {isShowError: true, roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER')},
      }),
      invalidatesTags: [ECacheTag.TENANT],
    }),
    getBillingFlag: builder.query<boolean, void | TItemId>({
      query: (tenantId) => ({
        method: 'GET',
        url: tenantId
          ? '/tenantmanagement/user/config/known/support'
          : '/tenantmanagement/user/config/known',
        params: {
          tenantId,
          code: EUserConfigCodes.TENANT_IS_BILLING_DISABLED,
        },
        meta: {isShowError: true},
      }),
      transformResponse: (flag: string | boolean) => {
        if (typeof flag === 'boolean') {
          return !flag;
        }

        return flag !== 'true';
      },
    }),
  }),
});

listenerMiddleware.startListening({
  matcher: isAllOf(
    api.endpoints.getCurrentUser.matchRejected,
    isMatchQueryMeta({isBackendError: true}),
  ),
  effect: ({payload}: AnyAction) => {
    if (payload.status !== 401) {
      showErrorNotification(payload);
    }
  },
});

export const useGetTenantUserQuery = (id?: TItemId | null) =>
  api.useGetAllTenantUsersQuery(undefined, {
    selectFromResult: ({data, ...rest}) => ({
      ...rest,
      data: data?.find((u) => u.id === id),
    }),
    skip: !id,
  });

export const {
  useGetCurrentUserQuery,
  useLazyGetCurrentUserQuery,
  useGetTenantDataQuery,
  useLazyGetTenantDataQuery,
  useGetUserConfigQuery,
  useSetUserConfigMutation,
  useGetBankDetailsQuery,
  useSetUserTimeZoneMutation,
  useGetFeatureFlagsQuery,
  useGetTenantUsersQuery,
  useGetWhatsAppTariffChangeBannerDatesQuery,
  useGetLowBalanceLimitsQuery,
  useGetSpecialOffersQuery,
  useSubmitSurveyAnswersMutation,
  useSetSurveyConsentMutation,
  useSendUserFeedbackMutation,
  useAcceptTariffsMutation,
  useGetBillingFlagQuery,
  endpoints: {getTenantData, getCurrentUser},
  endpoints: userEndpoints,
} = api;
